import { CommonModule, DatePipe } from '@angular/common';
import { inject, ModuleWithProviders, NgModule } from '@angular/core';
import { ErrorPolicy, InMemoryCache, WatchQueryFetchPolicy } from '@apollo/client/core';
import { StoreModule } from '@ngrx/store';
import {
  AuthService,
  PlatformAuthService,
  PlatformBrowserService,
  PlatformInfoService,
  PlatformSelectorOrganizationService,
  URL_WEB_BFF,
  URL_BILLING_API,
  URL_BILLING_CALCULATION,
  URL_DOCUSIGN,
  URL_GATEWAY,
  URL_CONTENT_API,
  URL_NOTIFICATIONS_SERVICE,
  URL_TASK_MANAGER,
} from '@tgx/shared/services';
import { provideNamedApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { environment } from './../../../../../environments/environment';
import { BillingGuard, BannerGuard } from './guards/_index.guards';
import { throwIfAlreadyLoaded } from './module-import-guard';
import { GatewaySwitcherService } from './services/gateway-switcher.service';
import { CapitalizePipe, NumberWithCommasPipe, RoundPipe, TimingPipe } from './pipes/index_pipes';
import { JwtPermissionsService, MenuService, WebAdminService } from './services/_index-core.services';
import { GqlContextName } from '@tgx/shared/data-access/graphql';
import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';

import { provideOptimus } from '@openng/optimus-ui/config';
import { definePreset } from '@openng/optimus-ui-themes';
import Material from '@openng/optimus-ui-themes/material';

const CustomTheme = definePreset(Material, {
  semantic: {
    primary: {
      // COLOR PRINCIPAL
      50: '#e3f2ff',
      100: '#b3d9ff',
      200: '#80bfff',
      300: '#4da6ff',
      400: '#1a8cff',
      500: '#0073e6', // color base
      600: '#005bb4',
      700: '#004282',
      800: '#002951',
      900: '#001021',
    },
  },
});

const PIPES = [CapitalizePipe, RoundPipe, TimingPipe, NumberWithCommasPipe];

const GUARDS = [BillingGuard, BannerGuard];

const SERVICES = [
  PlatformBrowserService,
  PlatformInfoService,
  PlatformAuthService,
  WebAdminService,
  MenuService,
  JwtPermissionsService,
  AuthService,
  DatePipe,
];

export const NB_CORE_PROVIDERS = [
  ...SERVICES,
  ...GUARDS,
  {
    provide: URL_WEB_BFF,
    useValue: environment.urlWebBff,
  },
  {
    provide: URL_BILLING_API,
    useValue: environment.apis.billing_api,
  },
  {
    provide: URL_BILLING_CALCULATION,
    useValue: environment.apis.billing_calculation,
  },
  {
    provide: URL_DOCUSIGN,
    useValue: environment.apis.docusign,
  },
  {
    provide: URL_GATEWAY,
    useFactory: () => GatewaySwitcherService.getStoredGatewayUrl(),
  },
  {
    provide: URL_CONTENT_API,
    useValue: environment.apis.content,
  },
  {
    provide: URL_NOTIFICATIONS_SERVICE,
    useValue: environment.apis.notifications_service,
  },
  {
    provide: URL_TASK_MANAGER,
    useValue: environment.apis.task_manager,
  },
  provideOptimus({
    theme: {
      preset: CustomTheme,
      options: {
        darkModeSelector: '.app-dark', // clase que activaría modo DARK si estuviera, sino se queda modo light.
      },
    },
    overlayOptions: {
      appendTo: 'body',
      autoZIndex: true,
      baseZIndex: 2000,
    },
  }),
  PlatformSelectorOrganizationService,
  provideHttpClient(withXhr(), withInterceptorsFromDi()),
  provideNamedApollo(() => {
    const queryPolicy: WatchQueryFetchPolicy = 'network-only';
    const errorPolicy: ErrorPolicy = 'all';
    const selectedGatewayUrl = GatewaySwitcherService.getStoredGatewayUrl();

    const httpLink = inject(HttpLink);

    const client = (uri: string) => ({
      link: httpLink.create({
        uri: uri,
      }),
      cache: new InMemoryCache({
        typePolicies: {
          Query: {
            fields: {
              activation: {
                merge: true,
              },
              admin: {
                merge: true,
              },
              alertsX: {
                merge: true,
              },
              application: {
                merge: true,
              },
              channelX: {
                merge: true,
              },
              businessRules: {
                merge: true,
              },
              hotelX: {
                merge: true,
              },
              infraestructure: {
                merge: true,
              },
              insights: {
                merge: true,
              },
              inventory: {
                merge: true,
              },
              logging: {
                merge: true,
              },
              package: {
                merge: true,
              },
              reconciliation: {
                merge: true,
              },
              searchCriteria: {
                merge: true,
              },
              social: {
                merge: true,
              },
              speed: {
                merge: true,
              },
              stats: {
                merge: true,
              },
              trafficOptimization: {
                merge: true,
              },
              transport: {
                merge: true,
              },
              data: {
                merge: true,
              },
            },
          },
        },
      }),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: queryPolicy,
          addTypename: false,
        },
        query: {
          fetchPolicy: queryPolicy,
          addTypename: false,
        },
        mutate: {
          errorPolicy: errorPolicy,
        },
      },
      addTypename: false,
    });

    const clients = {
      default: client(selectedGatewayUrl),
      [GqlContextName.GATEWAY]: client(selectedGatewayUrl),
      [GqlContextName.BILLING]: client(environment.apis.billing_api),
      [GqlContextName.DATA]: client(environment.apis.data),
      [GqlContextName.SOCIAL]: client(environment.apis.social),
      [GqlContextName.INSIGHTS]: client(environment.apis.insights_api),
    };

    return clients;
  }),
];

@NgModule({
  imports: [CommonModule, StoreModule],
  exports: [...PIPES],
  providers: NB_CORE_PROVIDERS,
  declarations: [...PIPES],
})
export class CoreModule {
  constructor() {
    const parentModule = inject(CoreModule, { optional: true, skipSelf: true });

    throwIfAlreadyLoaded(parentModule, 'CoreModule');
  }

  static forRoot(): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [...NB_CORE_PROVIDERS],
    };
  }
}
